import { AIField } from "./Main/components/AiSettings/config";
import { Field } from "./Main/components/AuthSettings/config";
import { GeneralField } from "./Main/components/GeneralSettings/config";
import { LoyaltyTiersField } from "./Main/components/LoyaltyTiersSettings/config";
import { DeliveryFeeField } from "./Main/components/MinimumOrderDeliveryFeeSettings/config";
import { MinimumValuesField } from "./Main/components/MinimumValues/config";
import { NotificationField } from "./Main/components/NotificationsSettings/config";
import { Payment } from "./Main/components/PaymentSettings/config";
import { Referral } from "./Main/components/ReferralsSettings/config";
import { RewardsField } from "./Main/components/RewardsSettings/config";
import { RoleSettingsField } from "./Main/components/RoleSettings/config";
import { ScheduleOrder } from "./Main/components/ScheduleOrdersSettings/config";

type Name =
  | string
  | Field
  | GeneralField
  | DeliveryFeeField
  | LoyaltyTiersField
  | MinimumValuesField
  | NotificationField
  | Payment
  | Referral
  | RewardsField
  | RoleSettingsField
  | ScheduleOrder
  | AIField;

export const humanizePath = (path: string): string => {
  const isReadable = /[\s]/.test(path); // якщо є пробіли, вважаємо рядок вже читабельним
  if (isReadable) return path;

  return path
    .replace(/\[(\d+)\]/g, (_, index) => ` ${Number(index) + 1}`)
    .split(".")
    .map(part =>
      part
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, str => str.toUpperCase())
        .trim(),
    )
    .join(" → ");
};

export const getSettingsName = (name: Name): string => {
  switch (name) {
    case Field.IsAppAccessible:
      return "Enable Mobile application";
    case Field.IsExternalFiatCentsPriceVisible:
      return "Display KWD prices";
    case Field.IsApplePayAllowed:
      return "Enable ApplePay payments";
    case Field.IsTabbyAllowed:
      return "Enable Tabby payments";
    case Field.IsTalyAllowed:
      return "Enable Taly payments";
    case Field.IsFoodLibraryEnabled:
      return "Enable Food Library";
    case Field.IsTamaraAllowed:
      return "Enable Tamara payments";
    case Field.IsComments:
      return "Enable rating and reviews";
    case Field.AllowAccountActivation:
      return "Enable invite friends code sharing";
    case Field.RequireInvitation:
      return "Require invitation to access app";
    case Field.AllowWaitingList:
      return "Require user activation";
    case Field.AllowCoupons:
      return "Enable purchase coupons codes";
    case Field.AllowTopUpCoupons:
      return "Enable top-up coupons codes";
    case Field.AllowMenuItemsGrid:
      return "Enable menu items grid";
    case GeneralField.ExchangeRateValue:
      return "Exchange rate";
    case GeneralField.BalanceExpirationDays:
      return "Virtual currency expiration time";
    case GeneralField.MinimumTopUpValue:
      return "Minimum top up (KWD)";
    case GeneralField.MinimumOrderValue:
      return "Minimum order (FM Coins)";
    case GeneralField.InstantOrderCancellationTime:
      return "Minimum instant order rejection time (minutes)";
    case GeneralField.DefaultPreparationTime:
      return "Default order preparation time";
    case GeneralField.DefaultDeliveryTime:
      return "Default order delivery time";
    case GeneralField.OrderTimeRangeBuffer:
      return "Pickup time range buffer";
    case GeneralField.IsStockActive:
      return "Restrict orders based on stock availability";
    case GeneralField.IsCalorieBankFeatureEnabled:
      return "Enable Сalorie bank feature";
    case GeneralField.IsCalorieBankFeatureForUsersEnabled:
      return "Enable Enable Сalorie for all users";
    case AIField.IsAIAssistantAllowed:
      return "AI Assistant feature";
    case AIField.IsAIAssistantForUsersAllowed:
      return "Enable AI features for all users";
    case AIField.AiMode:
      return "AI mode";
    case DeliveryFeeField.DeliveryFeeSettings:
      return "Enable order delivery fee";
    case LoyaltyTiersField.IsLoyaltyFeatureEnabled:
      return "Enable loyalty tier system";
    case MinimumValuesField.MinimumOrderValue:
      return "Order (FM coins)";
    case MinimumValuesField.MinimumTopUpValue:
      return "Top up (KWD)";
    case NotificationField.IsNotificationsEnabled:
      return "Enable Mobile App notifications settings";
    case Payment.IsCashPayment:
      return "Enable cash payments";
    case Payment.IsCashPaymentForColdKitchen:
      return "Cold kitchen with cash payment for selected customers";
    case Payment.AvailableLocations:
      return "Kitchen status (Busy/Not Busy)";
    case Payment.MinimumOrder:
      return "Minimum order (KWD)";
    case Payment.DeliveryFeeFiatCents:
      return "Delivery fee (KWD)";
    case Payment.isDeliveryFeeAllowed:
      return "Enable delivery fee";
    case Payment.DeliveryFeeFmcCents:
      return "Delivery fee (FM Coins)";
    case Payment.Discount:
      return "Discount (%)";
    case Referral.IsReferralBonus:
      return "Discount (%)";
    case Referral.BonusForInviter:
      return "Referrer bonus (FM Coins)";
    case Referral.BonusForInvitee:
      return "Referral bonus (FM Coins)";
    case RewardsField.IsRewardsEnabled:
      return "Enable rewards";
    case RewardsField.WeekStartDay:
      return "Reward week start day";
    case RewardsField.BonusCoins:
      return "Coins reward";
    case RewardsField.MetricsMinRequired:
      return "Required metrics count";
    case RewardsField.StepMetricRequiredStepsPerDay:
      return "Steps count";
    case RewardsField.StepMetricRequiredStepsDays:
      return "Days count";
    case RewardsField.ExerciseMetricMinutesCount:
      return "Minutes count";
    case RewardsField.DiaryMetricDaysCount:
      return "Minutes count";
    case RoleSettingsField.AgentRoleRefundAddLimit:
      return "Agent role refund (add) limit";
    case RoleSettingsField.AgentRoleBalanceExpirationLimit:
      return "Agent role balance expiration limit";
    case RoleSettingsField.ManagerRoleRefundAddLimit:
      return "Manager role refund (add) limit";
    case RoleSettingsField.ManagerRoleBalanceExpirationLimit:
      return "Manager role balance expiration limit";
    case ScheduleOrder.IsScheduleOrders:
      return "Enable order schedule";
    case ScheduleOrder.TimeToNextSlot:
      return "Minimum time between order placed time and selected time slot";
    case ScheduleOrder.OrderCancelTime:
      return "Minimum scheduled order rejection time (minutes) before selected time slot starts";
    case ScheduleOrder.CancelTimeAfterStart:
      return "Minimum scheduled order rejection time (minutes) before starting order preparation";
    case ScheduleOrder.ScheduleOrderTime:
      return "Maximum available order time (days)";
    case ScheduleOrder.OrderAppearingTime:
      return "Order appearing time (minutes)";

    default:
      return humanizePath(name);
  }
};
